import * as THREE from 'three';
import { DebugWidget, createWidgetContainer } from './DebugOverlay';
import { updatePointCloudsProfile, updateVisibilityProfile } from '../Potree_update_visibility';
import type { FloorDetectionFixture } from './floorDetection';

interface DynamicPointBudgetStats {
  currentFPS: number;
  targetFPS: number;
  refreshRate: number | null;
  stability: number;
}

interface FrameProfile {
  total: number;
  workerProcess?: number;
  update?: number;
  render?: number;
  betweenFrames?: number;
  updateFreezeBlock?: number;
  updatePointClouds?: number;
  updateMaterials?: number;
  updateControls?: number;
  updateClipBoxes?: number;
  updateAnnotations?: number;
  updatePlanes?: number;
  updateOther?: number;
}

interface PointCloudEntry {
  numVisiblePoints?: number;
}

interface ViewerScene {
  pointclouds?: PointCloudEntry[];
  scene?: THREE.Scene;
}

interface DebugViewer {
  fps?: number;
  instantFps?: number;
  frameProfile?: FrameProfile;
  isSoftwareRenderer?: boolean;
  scene?: ViewerScene;
  /** Overlay scene rendered on top of the point cloud pass — the floor
   *  detection surface-sample debug layers live here. */
  measurementScene?: THREE.Scene;
  renderer?: THREE.WebGLRenderer;
  getDynamicPointBudgetStats(): DynamicPointBudgetStats | null;
  isDynamicPointBudgetEnabled(): boolean;
  getPointBudget(): number;
}

interface PlaneProfilingStat {
  avg: number;
}

/**
 * Configuration for a data series in a graph
 */
interface GraphSeries {
  name: string;
  color: string;
  data: number[];
  maxHistory: number;
}

/**
 * Configuration for a target line on the graph
 */
interface GraphTarget {
  value: number;
  label: string;
  color: string;
  dashed?: boolean;
  labelPosition?: 'above' | 'below';
}

/**
 * Configuration for a reference line (grid line)
 */
interface GraphReference {
  value: number;
  label?: string;
  color?: string;
}

/**
 * Reusable scrolling line graph widget
 */
class ScrollingGraphWidget {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private series: Map<string, GraphSeries> = new Map();
  private targets: GraphTarget[] = [];
  private references: GraphReference[] = [];
  private yMin: number;
  private yMax: number;
  private footer = '';

  constructor(width = 360, height = 100, yMin = 0, yMax = 100) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = width;
    this.canvas.height = height;
    this.canvas.style.cssText = `
      width: 100%;
      height: ${height}px;
      background: rgba(0, 0, 0, 0.3);
      border-radius: 4px;
    `;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get 2d canvas context');
    }
    this.ctx = ctx;
    this.yMin = yMin;
    this.yMax = yMax;
  }

  getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  addSeries(name: string, color: string, maxHistory = 100): void {
    this.series.set(name, {
      name,
      color,
      data: [],
      maxHistory,
    });
  }

  addDataPoint(seriesName: string, value: number): void {
    const series = this.series.get(seriesName);
    if (!series) return;

    series.data.push(value);
    if (series.data.length > series.maxHistory) {
      series.data.shift();
    }
  }

  setTargets(targets: GraphTarget[]): void {
    this.targets = targets;
  }

  setReferences(references: GraphReference[]): void {
    this.references = references;
  }

  setFooter(text: string): void {
    this.footer = text;
  }

  setYRange(yMin: number, yMax: number): void {
    this.yMin = yMin;
    this.yMax = yMax;
  }

  /**
   * Draw a value overlay in the top right corner
   */
  private drawValueOverlay(seriesName: string, formatter: (value: number) => string, colorFn?: (value: number) => string): void {
    const series = this.series.get(seriesName);
    if (!series || series.data.length === 0) return;

    const ctx = this.ctx;
    const { width } = this.canvas;
    const currentValue = series.data[series.data.length - 1];
    const color = colorFn ? colorFn(currentValue) : series.color;

    ctx.fillStyle = color;
    ctx.font = 'bold 14px monospace';
    ctx.fillText(formatter(currentValue), width - 80, 20);
  }

  draw(valueOverlays?: Array<{ seriesName: string; formatter: (value: number) => string; colorFn?: (value: number) => string }>): void {
    const { width, height } = this.canvas;
    const ctx = this.ctx;

    // Clear canvas fully (no ghosting/trail effect)
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(0, 0, width, height);

    const range = this.yMax - this.yMin;

    // Draw reference lines (grid)
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.lineWidth = 1;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.font = '9px monospace';

    this.references.forEach((ref) => {
      if (ref.value < this.yMin || ref.value > this.yMax) return;
      const y = height - ((ref.value - this.yMin) / range) * height;

      ctx.strokeStyle = ref.color || 'rgba(255, 255, 255, 0.1)';
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();

      if (ref.label) {
        ctx.fillText(ref.label, 5, y - 2);
      }
    });

    // Draw target lines
    this.targets.forEach((target) => {
      if (target.value < this.yMin || target.value > this.yMax) return;
      const y = height - ((target.value - this.yMin) / range) * height;

      ctx.strokeStyle = target.color;
      ctx.lineWidth = 2;
      if (target.dashed) {
        ctx.setLineDash([5, 3]);
      }
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw label
      ctx.fillStyle = target.color;
      ctx.font = 'bold 10px monospace';
      const labelY = target.labelPosition === 'above' ? y - 5 : y + 12;
      ctx.fillText(target.label, width - 75, labelY);
    });

    // Draw data series
    this.series.forEach((series) => {
      if (series.data.length < 2) return;

      ctx.strokeStyle = series.color;
      ctx.lineWidth = 2;
      ctx.beginPath();

      const step = width / series.maxHistory;

      series.data.forEach((value, i) => {
        const x = i * step;
        const clampedValue = Math.max(this.yMin, Math.min(this.yMax, value));
        const y = height - ((clampedValue - this.yMin) / range) * height;

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });

      ctx.stroke();
    });

    // Draw value overlays
    if (valueOverlays) {
      valueOverlays.forEach((overlay) => {
        this.drawValueOverlay(overlay.seriesName, overlay.formatter, overlay.colorFn);
      });
    }

    // Draw footer
    if (this.footer) {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
      ctx.font = '10px monospace';
      ctx.fillText(this.footer, 5, height - 5);
    }
  }

  clear(): void {
    this.series.forEach((series) => {
      series.data = [];
    });
  }
}

function queryEl<T extends HTMLElement>(parent: HTMLElement, selector: string): T {
  const el = parent.querySelector<T>(selector);
  if (!el) {
    throw new Error(`Element not found: ${selector}`);
  }
  return el;
}

/**
 * FPS and Point Budget Stats Widget
 */
export class FPSStatsWidget implements DebugWidget {
  name = 'fps-stats';
  container: HTMLElement;
  private viewer: DebugViewer;
  private fpsEl: HTMLElement;
  private currentFpsEl: HTMLElement;
  private targetFpsEl: HTMLElement;
  private refreshRateEl: HTMLElement;
  private pointBudgetEl: HTMLElement;
  private visiblePointsEl: HTMLElement;
  private dynamicBudgetEl: HTMLElement;
  private stabilityEl: HTMLElement;

  constructor(viewer: DebugViewer) {
    this.viewer = viewer;
    this.container = createWidgetContainer('📊 Performance Stats');

    const content = document.createElement('div');
    content.style.cssText = 'line-height: 1.6;';
    content.innerHTML = `
      <div><strong>Display FPS:</strong> <span id="debug-fps">--</span></div>
      <div><strong>Current FPS:</strong> <span id="debug-current-fps">--</span></div>
      <div><strong>Target FPS:</strong> <span id="debug-target-fps">--</span></div>
      <div><strong>Refresh Rate:</strong> <span id="debug-refresh-rate">--</span></div>
      <div><strong>Point Budget:</strong> <span id="debug-point-budget">--</span></div>
      <div><strong>Visible Points:</strong> <span id="debug-visible-points">--</span></div>
      <div><strong>Dynamic Budget:</strong> <span id="debug-dynamic-budget">--</span></div>
      <div><strong>Stability:</strong> <span id="debug-stability">--</span></div>
    `;

    this.container.appendChild(content);

    this.fpsEl = queryEl(content, '#debug-fps');
    this.currentFpsEl = queryEl(content, '#debug-current-fps');
    this.targetFpsEl = queryEl(content, '#debug-target-fps');
    this.refreshRateEl = queryEl(content, '#debug-refresh-rate');
    this.pointBudgetEl = queryEl(content, '#debug-point-budget');
    this.visiblePointsEl = queryEl(content, '#debug-visible-points');
    this.dynamicBudgetEl = queryEl(content, '#debug-dynamic-budget');
    this.stabilityEl = queryEl(content, '#debug-stability');
  }

  update(): void {
    // Display FPS (3-second average for legacy display)
    this.fpsEl.textContent = this.viewer.fps ? this.viewer.fps.toFixed(1) : '--';

    // Dynamic budget stats
    const stats = this.viewer.getDynamicPointBudgetStats();
    if (stats) {
      this.currentFpsEl.textContent = stats.currentFPS > 0 ? stats.currentFPS.toFixed(1) : '--';
      this.targetFpsEl.textContent = stats.targetFPS.toFixed(1);
      this.refreshRateEl.textContent = stats.refreshRate ? `${stats.refreshRate}Hz` : 'Detecting...';
      this.dynamicBudgetEl.textContent = this.viewer.isDynamicPointBudgetEnabled() ? '✅ Enabled' : '❌ Disabled';
      this.stabilityEl.textContent = `${stats.stability}/30`;
    } else {
      this.currentFpsEl.textContent = '--';
      this.targetFpsEl.textContent = '--';
      this.refreshRateEl.textContent = '--';
      this.dynamicBudgetEl.textContent = '❌ Disabled';
      this.stabilityEl.textContent = '--';
    }

    // Point budget
    const budget = this.viewer.getPointBudget();
    this.pointBudgetEl.textContent = this.formatNumber(budget);

    // Visible points with utilization percentage
    let totalVisible = 0;
    if (this.viewer.scene?.pointclouds) {
      this.viewer.scene.pointclouds.forEach((pc: PointCloudEntry) => {
        totalVisible += pc.numVisiblePoints || 0;
      });
    }
    const utilization = budget > 0 ? (totalVisible / budget) * 100 : 0;
    this.visiblePointsEl.textContent = `${this.formatNumber(totalVisible)} (${utilization.toFixed(0)}%)`;
  }

  private formatNumber(num: number): string {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(2) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  }

  destroy(): void {
    // Cleanup if needed
  }
}

/**
 * FPS Graph Widget with scrolling line chart
 */
export class FPSGraphWidget implements DebugWidget {
  name = 'fps-graph';
  container: HTMLElement;
  private viewer: DebugViewer;
  private graph: ScrollingGraphWidget;

  constructor(viewer: DebugViewer) {
    this.viewer = viewer;
    this.container = createWidgetContainer('📈 FPS Graph');

    // Create reusable graph
    this.graph = new ScrollingGraphWidget(360, 100, 0, 144);

    // Add FPS data series
    this.graph.addSeries('fps', '#4CAF50', 100);

    // Set up reference lines (grid)
    this.graph.setReferences([
      { value: 30, label: '30' },
      { value: 60, label: '60' },
      { value: 120, label: '120' },
      { value: 144, label: '144' },
    ]);

    this.container.appendChild(this.graph.getCanvas());
  }

  update(): void {
    // Use instantaneous FPS for per-frame accuracy to see spikes
    const fps = this.viewer.instantFps || 0;
    const stats = this.viewer.getDynamicPointBudgetStats();
    const targetFPS = stats?.targetFPS || 60;

    // Add data point
    this.graph.addDataPoint('fps', fps);

    // Update target line
    this.graph.setTargets([
      {
        value: targetFPS,
        label: `Target: ${targetFPS.toFixed(0)}`,
        color: '#FFC107',
        dashed: true,
        labelPosition: 'below',
      },
    ]);

    // Set footer text
    if (stats?.refreshRate) {
      this.graph.setFooter(`${stats.refreshRate}Hz Display`);
    }

    // Draw the graph with FPS value overlay
    this.graph.draw([
      {
        seriesName: 'fps',
        formatter: (value) => `${value.toFixed(1)} FPS`,
        colorFn: (value) => {
          const ratio = value / targetFPS;
          return ratio < 0.8 ? '#f44336' : ratio < 0.95 ? '#FFC107' : '#4CAF50';
        },
      },
    ]);
  }

  destroy(): void {
    this.graph.clear();
  }
}

/**
 * Frame Time Breakdown Widget - shows where time is spent each frame
 */
export class FrameTimeWidget implements DebugWidget {
  name = 'frame-time';
  container: HTMLElement;
  private viewer: DebugViewer;
  private graph: ScrollingGraphWidget;
  private statsEl: HTMLElement;

  constructor(viewer: DebugViewer) {
    this.viewer = viewer;
    this.container = createWidgetContainer('⏱️ Frame Time Breakdown');

    // Create graph for frame times (0-50ms range to see spikes)
    this.graph = new ScrollingGraphWidget(360, 100, 0, 50);

    // Add series for different parts of the frame
    this.graph.addSeries('total', '#f44336', 200); // Red for total
    this.graph.addSeries('worker', '#9C27B0', 200); // Purple for worker processing
    this.graph.addSeries('update', '#FF9800', 200); // Orange for full update
    this.graph.addSeries('render', '#4CAF50', 200); // Green for render

    // Reference lines at key frame time thresholds
    this.graph.setReferences([
      { value: 16.67, label: '60fps' },
      { value: 33.33, label: '30fps' },
    ]);

    this.container.appendChild(this.graph.getCanvas());

    // Add legend
    const legend = document.createElement('div');
    legend.style.cssText = 'margin-top: 8px; font-size: 11px;';
    legend.innerHTML = `
      <span style="color: #f44336;">■</span> Total &nbsp;
      <span style="color: #9C27B0;">■</span> Worker &nbsp;
      <span style="color: #FF9800;">■</span> Update &nbsp;
      <span style="color: #4CAF50;">■</span> Render
    `;
    this.container.appendChild(legend);

    // Add stats display
    this.statsEl = document.createElement('div');
    this.statsEl.style.cssText = 'margin-top: 4px; font-size: 10px; font-family: monospace;';
    this.container.appendChild(this.statsEl);
  }

  // Snapshot of the frame with the highest total time
  private maxFrame = {
    total: 0,
    worker: 0,
    update: 0,
    updatePC: 0,
    freezeBlock: 0,
    render: 0,
    between: 0,
    // Granular update breakdown
    materials: 0,
    controls: 0,
    clipBoxes: 0,
    annotations: 0,
    planes: 0,
    other: 0,
    // Granular PC breakdown
    pcVisibility: 0,
    pcMaterial: 0,
    pcBounds: 0,
    pcMemory: 0,
    // Granular visibility breakdown
    visLoop: 0,
    visIterations: 0,
    visTree: 0,
    visStructures: 0,
    visLoad: 0,
    // Inside-loop breakdown (accumulated per frame)
    loopGetBB: 0,
    loopFrustum: 0,
    loopClip: 0,
    loopChildren: 0,
    loopWeight: 0,
  };

  // Rolling history for averages
  private history: Array<{ total: number; worker: number; update: number; render: number }> = [];
  private readonly historySize = 100;

  update(): void {
    const profile = this.viewer.frameProfile;
    if (!profile) return;

    // Track max frame - capture all values from the frame with highest total
    if (profile.total > this.maxFrame.total) {
      this.maxFrame = {
        total: profile.total,
        worker: profile.workerProcess || 0,
        update: profile.update || 0,
        updatePC: profile.updatePointClouds || 0,
        freezeBlock: profile.updateFreezeBlock || 0,
        render: profile.render || 0,
        between: profile.betweenFrames || 0,
        // Granular update breakdown
        materials: profile.updateMaterials || 0,
        controls: profile.updateControls || 0,
        clipBoxes: profile.updateClipBoxes || 0,
        annotations: profile.updateAnnotations || 0,
        planes: profile.updatePlanes || 0,
        other: profile.updateOther || 0,
        // Granular PC breakdown
        pcVisibility: updatePointCloudsProfile.updateVisibility,
        pcMaterial: updatePointCloudsProfile.updateMaterial,
        pcBounds: updatePointCloudsProfile.updateVisibleBounds,
        pcMemory: updatePointCloudsProfile.freeMemory,
        // Granular visibility breakdown
        visLoop: updateVisibilityProfile.mainLoop,
        visIterations: updateVisibilityProfile.loopIterations,
        visTree: updateVisibilityProfile.toTreeNode,
        visStructures: updateVisibilityProfile.structures,
        visLoad: updateVisibilityProfile.nodeLoad,
        // Inside-loop breakdown
        loopGetBB: updateVisibilityProfile.loopGetBoundingBox,
        loopFrustum: updateVisibilityProfile.loopFrustumTest,
        loopClip: updateVisibilityProfile.loopIsClipped,
        loopChildren: updateVisibilityProfile.loopGetChildren,
        loopWeight: updateVisibilityProfile.loopChildWeight,
      };
    }

    // Track history for averages
    this.history.push({
      total: profile.total || 0,
      worker: profile.workerProcess || 0,
      update: profile.update || 0,
      render: profile.render || 0,
    });
    if (this.history.length > this.historySize) {
      this.history.shift();
    }

    // Add data points
    this.graph.addDataPoint('total', profile.total || 0);
    this.graph.addDataPoint('worker', profile.workerProcess || 0);
    this.graph.addDataPoint('update', profile.update || 0);
    this.graph.addDataPoint('render', profile.render || 0);

    // Calculate unaccounted time
    const accountedTime = (profile.update || 0) + (profile.render || 0) + (profile.workerProcess || 0);
    const unaccounted = (profile.total || 0) - accountedTime;

    // Helper to highlight values above threshold
    const highlight = (val: number, threshold: number) => (val > threshold ? `<span style="color: #ff6b6b; font-weight: bold;">${val.toFixed(1)}</span>` : val.toFixed(1));

    // Update stats display with granular breakdown
    const workerMs = profile.workerProcess || 0;
    const workerColor = workerMs > 5 ? '#9C27B0' : 'rgba(255,255,255,0.6)';

    // Get granular PC profiling
    const pcProfile = updatePointCloudsProfile;
    const visProfile = updateVisibilityProfile;

    this.statsEl.innerHTML = `
      <div>
        Total: <span style="color: ${profile.total > 16.67 ? '#f44336' : '#4CAF50'}">${profile.total?.toFixed(1) || 0}ms</span> |
        Worker: <span style="color: ${workerColor}">${workerMs.toFixed(1)}ms</span> |
        Update: <span style="color: #FF9800">${profile.update?.toFixed(1) || 0}ms</span> |
        Render: <span style="color: #4CAF50">${profile.render?.toFixed(1) || 0}ms</span>
      </div>
      <div style="margin-top: 2px; color: rgba(255,255,255,0.7); font-size: 9px;">
        Update: FB:${highlight(profile.updateFreezeBlock || 0, 10)}(PC:${highlight(profile.updatePointClouds || 0, 5)}) Mat:${highlight(profile.updateMaterials || 0, 2)} Ctrl:${highlight(profile.updateControls || 0, 2)} Clip:${highlight(profile.updateClipBoxes || 0, 2)} Ann:${highlight(profile.updateAnnotations || 0, 2)} Pln:${highlight(profile.updatePlanes || 0, 2)} Oth:${highlight(profile.updateOther || 0, 2)}
      </div>
      <div style="margin-top: 2px; color: rgba(255,255,255,0.6); font-size: 9px;">
        PC: Vis:${highlight(pcProfile.updateVisibility, 5)} Mat:${highlight(pcProfile.updateMaterial, 2)} Bnd:${highlight(pcProfile.updateVisibleBounds, 2)} Mem:${highlight(pcProfile.freeMemory, 2)}
      </div>
      <div style="margin-top: 2px; color: rgba(255,255,255,0.5); font-size: 9px;">
        Vis: Loop:${highlight(visProfile.mainLoop, 5)}(${visProfile.loopIterations}) Tree:${highlight(visProfile.toTreeNode, 2)} Strc:${highlight(visProfile.structures, 2)} Load:${highlight(visProfile.nodeLoad, 2)}
      </div>
      <div style="margin-top: 2px; color: rgba(255,255,255,0.4); font-size: 9px;">
        Loop: BB:${highlight(visProfile.loopGetBoundingBox, 5)} Frus:${highlight(visProfile.loopFrustumTest, 5)} Clip:${highlight(visProfile.loopIsClipped, 5)} Child:${highlight(visProfile.loopGetChildren, 5)} Wgt:${highlight(visProfile.loopChildWeight, 5)}
      </div>
      <div style="margin-top: 4px; border-top: 1px solid rgba(255,255,255,0.2); padding-top: 4px; color: rgba(255,255,255,0.5);">
        Max: <span style="color: ${this.maxFrame.total > 16.67 ? '#f44336' : '#4CAF50'}">${this.maxFrame.total.toFixed(1)}ms</span> |
        <span style="color: #9C27B0">${this.maxFrame.worker.toFixed(1)}ms</span> |
        <span style="color: #FF9800">${this.maxFrame.update.toFixed(1)}ms</span> |
        <span style="color: #4CAF50">${this.maxFrame.render.toFixed(1)}ms</span>
      </div>
      <div style="margin-top: 2px; color: rgba(255,255,255,0.5); font-size: 9px;">
        Max Update: FB:${highlight(this.maxFrame.freezeBlock, 10)}(PC:${highlight(this.maxFrame.updatePC, 5)}) Mat:${highlight(this.maxFrame.materials, 2)} Ctrl:${highlight(this.maxFrame.controls, 2)} Clip:${highlight(this.maxFrame.clipBoxes, 2)} Ann:${highlight(this.maxFrame.annotations, 2)} Pln:${highlight(this.maxFrame.planes, 2)} Oth:${highlight(this.maxFrame.other, 2)}
      </div>
      <div style="margin-top: 2px; color: rgba(255,255,255,0.5); font-size: 9px;">
        Max PC: Vis:${highlight(this.maxFrame.pcVisibility, 5)} Mat:${highlight(this.maxFrame.pcMaterial, 2)} Bnd:${highlight(this.maxFrame.pcBounds, 2)} Mem:${highlight(this.maxFrame.pcMemory, 2)}
      </div>
      <div style="margin-top: 2px; color: rgba(255,255,255,0.5); font-size: 9px;">
        Max Vis: Loop:${highlight(this.maxFrame.visLoop, 5)}(${this.maxFrame.visIterations}) Tree:${highlight(this.maxFrame.visTree, 2)} Strc:${highlight(this.maxFrame.visStructures, 2)} Load:${highlight(this.maxFrame.visLoad, 2)}
      </div>
      <div style="margin-top: 2px; color: rgba(255,255,255,0.4); font-size: 9px;">
        Max Loop: BB:${highlight(this.maxFrame.loopGetBB, 5)} Frus:${highlight(this.maxFrame.loopFrustum, 5)} Clip:${highlight(this.maxFrame.loopClip, 5)} Child:${highlight(this.maxFrame.loopChildren, 5)} Wgt:${highlight(this.maxFrame.loopWeight, 5)}
      </div>
      <div style="margin-top: 2px; color: ${unaccounted > 5 ? '#ff6b6b' : 'rgba(255,255,255,0.4)'};">
        Unaccounted: ${unaccounted.toFixed(1)}ms (cur) | Between: ${(profile.betweenFrames || 0).toFixed(1)}ms
      </div>
    `;

    // Draw the graph
    this.graph.draw();
  }

  destroy(): void {
    this.graph.clear();
  }
}

/**
 * Point Budget Graph Widget
 */
export class PointBudgetGraphWidget implements DebugWidget {
  name = 'point-budget-graph';
  container: HTMLElement;
  private viewer: DebugViewer;
  private graph: ScrollingGraphWidget;

  constructor(viewer: DebugViewer) {
    this.viewer = viewer;
    this.container = createWidgetContainer('📊 Point Budget vs Visible');

    // Create reusable graph with dynamic Y range (will be updated each frame)
    this.graph = new ScrollingGraphWidget(360, 100, 0, 20000000);

    // Add two data series
    this.graph.addSeries('budget', '#2196F3', 100);
    this.graph.addSeries('visible', '#FF9800', 100);

    this.container.appendChild(this.graph.getCanvas());

    // Add legend
    const legend = document.createElement('div');
    legend.style.cssText = 'margin-top: 8px; font-size: 11px;';
    legend.innerHTML = `
      <span style="color: #2196F3;">■</span> Budget &nbsp;
      <span style="color: #FF9800;">■</span> Visible
    `;
    this.container.appendChild(legend);
  }

  update(): void {
    const budget = this.viewer.getPointBudget();

    let totalVisible = 0;
    if (this.viewer.scene?.pointclouds) {
      this.viewer.scene.pointclouds.forEach((pc: PointCloudEntry) => {
        totalVisible += pc.numVisiblePoints || 0;
      });
    }

    // Add data points
    this.graph.addDataPoint('budget', budget);
    this.graph.addDataPoint('visible', totalVisible);

    // Dynamically adjust Y range to fit data
    const maxValue = Math.max(budget, totalVisible, 1000000); // Minimum 1M
    this.graph.setYRange(0, maxValue * 1.1); // Add 10% padding

    // Add reference lines at nice round numbers
    const references: GraphReference[] = [];
    const step = maxValue > 10000000 ? 5000000 : maxValue > 5000000 ? 2000000 : 1000000;
    for (let i = step; i < maxValue; i += step) {
      references.push({
        value: i,
        label: `${(i / 1000000).toFixed(0)}M`,
      });
    }
    this.graph.setReferences(references);

    // Draw the graph
    this.graph.draw();
  }

  destroy(): void {
    this.graph.clear();
  }
}

/**
 * Renderer Info Widget
 */
export class RendererInfoWidget implements DebugWidget {
  name = 'renderer-info';
  container: HTMLElement;
  private viewer: DebugViewer;
  private rendererEl: HTMLElement;
  private vendorEl: HTMLElement;
  private softwareEl: HTMLElement;

  constructor(viewer: DebugViewer) {
    this.viewer = viewer;
    this.container = createWidgetContainer('🖥️ Renderer Info');

    const content = document.createElement('div');
    content.style.cssText = 'line-height: 1.6; font-size: 11px;';
    content.innerHTML = `
      <div><strong>Renderer:</strong> <span id="debug-renderer">--</span></div>
      <div><strong>Vendor:</strong> <span id="debug-vendor">--</span></div>
      <div><strong>Software Rendering:</strong> <span id="debug-software">--</span></div>
    `;

    this.container.appendChild(content);

    this.rendererEl = queryEl(content, '#debug-renderer');
    this.vendorEl = queryEl(content, '#debug-vendor');
    this.softwareEl = queryEl(content, '#debug-software');

    // Get renderer info once
    this.getRendererInfo();
  }

  private getRendererInfo(): void {
    try {
      const gl = this.viewer.renderer?.getContext();
      if (!gl) return;
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');

      if (debugInfo) {
        const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) as string;
        const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) as string;

        this.rendererEl.textContent = renderer || 'Unknown';
        this.vendorEl.textContent = vendor || 'Unknown';
      } else {
        this.rendererEl.textContent = 'Extension not available';
        this.vendorEl.textContent = 'Extension not available';
      }

      this.softwareEl.textContent = this.viewer.isSoftwareRenderer ? '⚠️ Yes' : '✅ No';
    } catch {
      this.rendererEl.textContent = 'Error getting info';
      this.vendorEl.textContent = 'Error getting info';
    }
  }

  update(): void {
    // Static info, no need to update
  }

  destroy(): void {
    // Cleanup if needed
  }
}

interface SceneObject {
  name: string;
  visible: boolean;
  children?: SceneObject[];
}

/**
 * Floor Visualization Widget
 * Controls visibility of debug visualizations (points, rays, planes)
 */
export class FloorVisualizationWidget implements DebugWidget {
  name = 'floor-visualization';
  container: HTMLElement;
  private viewer: DebugViewer;
  private pointsCheckbox: HTMLInputElement;
  private patchesCheckbox: HTMLInputElement;
  private planesCheckbox: HTMLInputElement;
  private disqualifiedPointsCheckbox: HTMLInputElement;
  private pointsCountEl: HTMLElement;
  private patchesCountEl: HTMLElement;
  private planesCountEl: HTMLElement;
  private disqualifiedPointsCountEl: HTMLElement;
  private exportFixtureButton: HTMLButtonElement;
  /** Last finished detection run, provided by the owning tool; null before the first run. */
  private readonly getDetectionFixture: (() => FloorDetectionFixture | null) | null;

  // Tracked visualization names in the scene
  private readonly POINTS_NAME = 'floor-detection-points-debug';
  private readonly PATCHES_NAME = 'floor-detection-patches-debug';
  private readonly PLANES_NAME = 'floor-detection-planes-debug';
  private readonly DISQUALIFIED_POINTS_NAME = 'floor-detection-disqualified-points-debug';

  constructor(viewer: DebugViewer, getDetectionFixture?: () => FloorDetectionFixture | null) {
    this.viewer = viewer;
    this.getDetectionFixture = getDetectionFixture ?? null;
    this.container = createWidgetContainer('🔍 Floor Visualization');

    const content = document.createElement('div');
    content.style.cssText = 'line-height: 1.8;';

    // Create toggle controls
    content.innerHTML = `
      <div style="display: flex; align-items: center; margin-bottom: 8px;">
        <label style="display: flex; align-items: center; cursor: pointer; flex: 1;">
          <input type="checkbox" id="debug-toggle-points" style="margin-right: 8px; cursor: pointer;" />
          <span>Column surfaces</span>
        </label>
        <span id="debug-points-count" style="color: rgba(255, 255, 255, 0.6); font-size: 11px;">--</span>
      </div>
      <div style="display: flex; align-items: center; margin-bottom: 8px;">
        <label style="display: flex; align-items: center; cursor: pointer; flex: 1;">
          <input type="checkbox" id="debug-toggle-disqualified-points" style="margin-right: 8px; cursor: pointer;" />
          <span>Rejected surfaces</span>
        </label>
        <span id="debug-disqualified-points-count" style="color: rgba(255, 255, 255, 0.6); font-size: 11px;">--</span>
      </div>
      <div style="display: flex; align-items: center; margin-bottom: 8px;">
        <label style="display: flex; align-items: center; cursor: pointer; flex: 1;">
          <input type="checkbox" id="debug-toggle-patches" style="margin-right: 8px; cursor: pointer;" />
          <span>Room patches</span>
        </label>
        <span id="debug-patches-count" style="color: rgba(255, 255, 255, 0.6); font-size: 11px;">--</span>
      </div>
      <div style="display: flex; align-items: center; margin-bottom: 8px;">
        <label style="display: flex; align-items: center; cursor: pointer; flex: 1;">
          <input type="checkbox" id="debug-toggle-planes" style="margin-right: 8px; cursor: pointer;" />
          <span>Storey planes</span>
        </label>
        <span id="debug-planes-count" style="color: rgba(255, 255, 255, 0.6); font-size: 11px;">--</span>
      </div>
      <button id="debug-export-fixture" style="width: 100%; margin-top: 8px; padding: 6px 10px; background: #4CAF50; color: #fff; border: none; border-radius: 4px; font-size: 12px;">
        Export test fixture (.json)
      </button>
      <div style="margin-top: 12px; padding-top: 8px; border-top: 1px solid rgba(255, 255, 255, 0.1); font-size: 11px; color: rgba(255, 255, 255, 0.6);">
        <em>New visualizations auto-replace old ones</em>
      </div>
    `;

    this.container.appendChild(content);

    // Get references to checkboxes and count elements
    this.pointsCheckbox = queryEl<HTMLInputElement>(content, '#debug-toggle-points');
    this.disqualifiedPointsCheckbox = queryEl<HTMLInputElement>(content, '#debug-toggle-disqualified-points');
    this.patchesCheckbox = queryEl<HTMLInputElement>(content, '#debug-toggle-patches');
    this.planesCheckbox = queryEl<HTMLInputElement>(content, '#debug-toggle-planes');
    this.pointsCountEl = queryEl(content, '#debug-points-count');
    this.disqualifiedPointsCountEl = queryEl(content, '#debug-disqualified-points-count');
    this.patchesCountEl = queryEl(content, '#debug-patches-count');
    this.planesCountEl = queryEl(content, '#debug-planes-count');
    this.exportFixtureButton = queryEl<HTMLButtonElement>(content, '#debug-export-fixture');
    this.exportFixtureButton.addEventListener('click', () => this.exportFixture());

    // Add event listeners
    this.pointsCheckbox.addEventListener('change', () => this.togglePoints());
    this.disqualifiedPointsCheckbox.addEventListener('change', () => this.toggleDisqualifiedPoints());
    this.patchesCheckbox.addEventListener('change', () => this.togglePatches());
    this.planesCheckbox.addEventListener('change', () => this.togglePlanes());

    // Initialize state from current scene
    this.updateCheckboxStates();
  }

  private updateCheckboxStates(): void {
    const pointsObj = this.findVisualizationObject(this.POINTS_NAME);
    const disqualifiedPointsObj = this.findVisualizationObject(this.DISQUALIFIED_POINTS_NAME);
    const patchesObj = this.findVisualizationObject(this.PATCHES_NAME);
    const planesObj = this.findVisualizationObject(this.PLANES_NAME);

    this.pointsCheckbox.checked = pointsObj?.visible ?? false;
    this.disqualifiedPointsCheckbox.checked = disqualifiedPointsObj?.visible ?? false;
    this.patchesCheckbox.checked = patchesObj?.visible ?? false;
    this.planesCheckbox.checked = planesObj?.visible ?? false;
  }

  private findVisualizationObject(name: string): SceneObject | undefined {
    // Patch/plane groups live in the main scene; the surface-sample layers
    // live in the measurement overlay scene (drawn on top of the cloud).
    const fromScene = this.viewer.scene?.scene ? (this.viewer.scene.scene.children as SceneObject[]).find((child: SceneObject) => child.name === name) : undefined;
    if (fromScene) return fromScene;
    return this.viewer.measurementScene ? (this.viewer.measurementScene.children as SceneObject[]).find((child: SceneObject) => child.name === name) : undefined;
  }

  private togglePoints(): void {
    const obj = this.findVisualizationObject(this.POINTS_NAME);
    if (obj) {
      obj.visible = this.pointsCheckbox.checked;
    }
  }

  private toggleDisqualifiedPoints(): void {
    const obj = this.findVisualizationObject(this.DISQUALIFIED_POINTS_NAME);
    if (obj) {
      obj.visible = this.disqualifiedPointsCheckbox.checked;
    }
  }

  private togglePatches(): void {
    const obj = this.findVisualizationObject(this.PATCHES_NAME);
    if (obj) {
      obj.visible = this.patchesCheckbox.checked;
    }
  }

  private togglePlanes(): void {
    const obj = this.findVisualizationObject(this.PLANES_NAME);
    if (obj) {
      obj.visible = this.planesCheckbox.checked;
    }
  }

  /**
   * Download the last detection run as a regression fixture: the stage-1
   * histogram grid (input) + resolved tuning + detected levels (expected
   * output). The owning tool snapshots the fixture the moment detection
   * completes, so later in-app plane edits never leak into it. Drop the file
   * into floorDetection/__tests__/fixtures/ to make it a jest regression
   * test (fixtureReplay.spec.ts).
   */
  private exportFixture(): void {
    const fixture = this.getDetectionFixture?.() ?? null;
    if (!fixture) return;
    const blob = new Blob([JSON.stringify(fixture)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `floor-detection-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private countChildren(obj: SceneObject): number {
    if (!obj) return 0;
    // Point-cloud groups hold one THREE.Points child — report the sample
    // count the builder stashed instead of "1 item".
    const debugCount = (obj as { userData?: { debugCount?: number } }).userData?.debugCount;
    return debugCount ?? obj.children?.length ?? 0;
  }

  update(): void {
    // Update counts
    const pointsObj = this.findVisualizationObject(this.POINTS_NAME);
    const disqualifiedPointsObj = this.findVisualizationObject(this.DISQUALIFIED_POINTS_NAME);
    const patchesObj = this.findVisualizationObject(this.PATCHES_NAME);
    const planesObj = this.findVisualizationObject(this.PLANES_NAME);

    this.pointsCountEl.textContent = pointsObj ? `${this.countChildren(pointsObj)} items` : 'None';
    this.disqualifiedPointsCountEl.textContent = disqualifiedPointsObj ? `${this.countChildren(disqualifiedPointsObj)} items` : 'None';
    this.patchesCountEl.textContent = patchesObj ? `${this.countChildren(patchesObj)} items` : 'None';
    this.planesCountEl.textContent = planesObj ? `${this.countChildren(planesObj)} items` : 'None';

    // Sync checkbox states if objects were added/removed externally
    if (pointsObj && this.pointsCheckbox.checked !== pointsObj.visible) {
      this.pointsCheckbox.checked = pointsObj.visible;
    }
    if (disqualifiedPointsObj && this.disqualifiedPointsCheckbox.checked !== disqualifiedPointsObj.visible) {
      this.disqualifiedPointsCheckbox.checked = disqualifiedPointsObj.visible;
    }
    if (patchesObj && this.patchesCheckbox.checked !== patchesObj.visible) {
      this.patchesCheckbox.checked = patchesObj.visible;
    }
    if (planesObj && this.planesCheckbox.checked !== planesObj.visible) {
      this.planesCheckbox.checked = planesObj.visible;
    }

    const exportable = (this.getDetectionFixture?.() ?? null) !== null;
    this.exportFixtureButton.disabled = !exportable;
    this.exportFixtureButton.style.opacity = exportable ? '1' : '0.4';
    this.exportFixtureButton.style.cursor = exportable ? 'pointer' : 'not-allowed';
  }

  destroy(): void {
    // No cleanup needed - visualizations remain in scene
  }
}

declare global {
  interface Window {
    DEBUG_PLANE_VISUALIZATION?: boolean;
    DEBUG_PLANE_PROFILING?: boolean;
    DEBUG_PLANE_PROFILING_STATS?: Record<string, PlaneProfilingStat>;
  }
}

/**
 * Plane Creator Widget
 * Debug controls for PlaneCreator visualization and profiling
 */
export class PlaneCreatorWidget implements DebugWidget {
  name = 'plane-creator';
  container: HTMLElement;
  private visualizationCheckbox: HTMLInputElement;
  private profilingCheckbox: HTMLInputElement;
  private profilingStatsEl: HTMLElement;

  constructor(_viewer: DebugViewer) {
    this.container = createWidgetContainer('✈️ Plane Creator Debug');

    const content = document.createElement('div');
    content.style.cssText = 'line-height: 1.8;';

    content.innerHTML = `
      <div style="display: flex; align-items: center; margin-bottom: 8px;">
        <label style="display: flex; align-items: center; cursor: pointer; flex: 1;">
          <input type="checkbox" id="debug-plane-visualization" style="margin-right: 8px; cursor: pointer;" />
          <span>Show Debug Visualization</span>
        </label>
      </div>
      <div style="display: flex; align-items: center; margin-bottom: 12px;">
        <label style="display: flex; align-items: center; cursor: pointer; flex: 1;">
          <input type="checkbox" id="debug-plane-profiling" style="margin-right: 8px; cursor: pointer;" />
          <span>Enable Profiling</span>
        </label>
      </div>
      <div id="profiling-stats" style="margin-top: 8px; padding-top: 8px; border-top: 1px solid rgba(255, 255, 255, 0.1); font-size: 11px; color: rgba(255, 255, 255, 0.6); line-height: 1.6; display: none;">
        <div style="margin-bottom: 6px; font-weight: bold; color: rgba(255, 255, 255, 0.8);">Performance (avg):</div>
        <div id="profiling-stats-content"></div>
      </div>
    `;

    this.container.appendChild(content);

    this.visualizationCheckbox = queryEl<HTMLInputElement>(content, '#debug-plane-visualization');
    this.profilingCheckbox = queryEl<HTMLInputElement>(content, '#debug-plane-profiling');
    this.profilingStatsEl = queryEl(content, '#profiling-stats');

    this.visualizationCheckbox.addEventListener('change', () => this.toggleVisualization());
    this.profilingCheckbox.addEventListener('change', () => this.toggleProfiling());

    // Initialize global flags
    if (typeof window.DEBUG_PLANE_VISUALIZATION === 'undefined') {
      window.DEBUG_PLANE_VISUALIZATION = false;
    }
    if (typeof window.DEBUG_PLANE_PROFILING === 'undefined') {
      window.DEBUG_PLANE_PROFILING = false;
    }
    if (typeof window.DEBUG_PLANE_PROFILING_STATS === 'undefined') {
      window.DEBUG_PLANE_PROFILING_STATS = {};
    }

    this.visualizationCheckbox.checked = window.DEBUG_PLANE_VISUALIZATION ?? false;
    this.profilingCheckbox.checked = window.DEBUG_PLANE_PROFILING ?? false;
  }

  private toggleVisualization(): void {
    window.DEBUG_PLANE_VISUALIZATION = this.visualizationCheckbox.checked;
  }

  private toggleProfiling(): void {
    window.DEBUG_PLANE_PROFILING = this.profilingCheckbox.checked;

    // Clear stats when disabling
    if (!this.profilingCheckbox.checked) {
      window.DEBUG_PLANE_PROFILING_STATS = {};
    }
  }

  update(): void {
    // Sync checkbox states if flags changed externally
    if (typeof window.DEBUG_PLANE_VISUALIZATION !== 'undefined') {
      this.visualizationCheckbox.checked = window.DEBUG_PLANE_VISUALIZATION;
    }
    if (typeof window.DEBUG_PLANE_PROFILING !== 'undefined') {
      this.profilingCheckbox.checked = window.DEBUG_PLANE_PROFILING;
    }

    // Update profiling stats display
    if (this.profilingCheckbox.checked && window.DEBUG_PLANE_PROFILING_STATS) {
      this.profilingStatsEl.style.display = 'block';

      const stats = window.DEBUG_PLANE_PROFILING_STATS;
      const statsContent = this.profilingStatsEl.querySelector('#profiling-stats-content');
      if (!statsContent) return;

      // Get key operations to display
      const keyOperations = ['adjustActivePlaneToMousePosition', 'getPointsAroundMouse', 'collectPointsFromCache', 'getRandomPointsFromClusters', 'extractPointsFromSegment', 'setRotationFromPoints'];

      let html = '';
      for (const op of keyOperations) {
        if (stats[op]) {
          const stat = stats[op];
          const avgMs = stat.avg.toFixed(2);
          const color = stat.avg < 5 ? '#4ade80' : stat.avg < 15 ? '#fbbf24' : '#ef4444';
          html += `<div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
            <span style="flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" title="${op}">${op.replace(/([A-Z])/g, ' $1').trim()}:</span>
            <span style="color: ${color}; font-weight: bold; margin-left: 8px;">${avgMs}ms</span>
          </div>`;
        }
      }

      statsContent.innerHTML = html || '<div style="color: rgba(255, 255, 255, 0.4);">No data yet...</div>';
    } else {
      this.profilingStatsEl.style.display = 'none';
    }
  }

  destroy(): void {
    // Cleanup global flags
    delete window.DEBUG_PLANE_VISUALIZATION;
    delete window.DEBUG_PLANE_PROFILING;
    delete window.DEBUG_PLANE_PROFILING_STATS;
  }
}
